import { datadogLogger } from '@fanduel/casino-datadog-logger';
import { useLayoutEffect } from 'react';

import config from '@/config';
import { buildTelemetryContext } from './telemetry-context';
import { DatadogSite } from './types';
import { markVitalsLoggerReady } from './vitals';
import { useShallow } from 'zustand/react/shallow';
import { useStore, type RootState } from '@/store/useStore';

const getLoggerContext = (runtime: RootState['runtime']) => {
  const { region, product, platform, brand } = runtime;

  return {
    region: region?.toLocaleUpperCase(),
    product: product?.toUpperCase(),
    platform: platform?.toUpperCase(),
    brand: brand?.toUpperCase(),
  };
};

export const useInitLogging = () => {
  const runtime = useStore(useShallow((s) => s.runtime));
  /** Primitives only — avoids effect re-running every render when session object identity changes. */
  const { sessionId, userId, hasActiveSession } = useStore(
    (s) => s.userSessionContext,
  );

  useLayoutEffect(() => {
    const gameSessionContext = getLoggerContext(runtime);

    const extraConfig = {
      globalContextRumEvents: {
        ...gameSessionContext,
      },
      sessionContext: buildTelemetryContext(),
      doNotLogList: [
        'csp_violation',
        'Failed to start PX: CorePx failed to load script',
      ],
      blackListedAttributesList: [],
      debugMode: process.env.DISABLE_LOGGING === 'true',
    };

    datadogLogger.initialize(
      {
        applicationId: config.rum.applicationId,
        clientToken: config.rum.clientToken,
        site: config.rum.site as DatadogSite,
        service: config.rum.service,
        env: process.env.NODE_CONFIG_ENV,
        version: process.env.BUILD_VERSION,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 0,
        trackUserInteractions: true,
        defaultPrivacyLevel: 'mask-user-input',
        silentMultipleInit: true,
      },
      extraConfig,
    );
    markVitalsLoggerReady();
  }, [runtime, sessionId, userId, hasActiveSession]);
};
